import { useGenerate, useGenerations } from '@studio/api-client';
import {
  GENERATE_TASKS,
  type GenerateTask,
  type GenerationJob,
  PROMPT_MAX_LENGTH,
  type UniversalProduct,
} from '@studio/contract';
import { Button, Chip, SidePanelModal, Textarea } from '@studio/ui';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { humanizeError } from '../lib/error-copy';
import { track } from '../observability';

export interface StudioModalProps {
  product: UniversalProduct | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

const TASK_LABELS: Record<GenerateTask, string> = {
  enhance: 'Enhance',
  edit: 'Edit',
  'match-style': 'Match style',
  merge: 'Merge',
};

const TASK_HINTS: Record<GenerateTask, string> = {
  enhance: 'Polish the product photo — lighting, sharpness, colour balance.',
  edit: 'Apply a textual edit to the photo, keeping the product intact.',
  'match-style': 'Render the product in a different scene that matches a style.',
  merge: 'Composite the product into a new background.',
};

/**
 * Studio v0 — prompt + mode picker → stub generation. The output renders
 * inline below the form; close + reopen to start a new generation. Real AI
 * integration lands in a follow-up; the wire shape from `useGenerate` stays
 * compatible (GenerationJob), so when the backend swaps the stub for a real
 * provider, this UI doesn't change.
 *
 * Triggered from a Products card click (or future Gallery picker).
 */
export function StudioModal({ product, open, onOpenChange }: StudioModalProps) {
  const generate = useGenerate();
  const generations = useGenerations();
  const navigate = useNavigate();
  const [task, setTask] = useState<GenerateTask>('enhance');
  const [prompt, setPrompt] = useState('');
  const [copied, setCopied] = useState(false);

  // Reset form whenever the modal reopens.
  useEffect(() => {
    if (!open) return;
    setTask('enhance');
    setPrompt('');
    setCopied(false);
    generate.reset();
    if (product) track('product.studio_opened', { productUid: product.uid });
  }, [open, product, generate.reset]);

  const productDrafts = useMemo<GenerationJob[]>(() => {
    if (!product || !generations.data) return [];
    return generations.data.items
      .filter((g) => g.productUid === product.uid && g.outputImageUrl)
      .slice(0, 8);
  }, [product, generations.data]);

  function loadDraft(draft: GenerationJob) {
    setTask(draft.task);
    setPrompt(draft.originalPrompt);
    generate.reset();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!product) return;
    const job = await generate.mutateAsync({
      productUid: product.uid,
      task,
      prompt: prompt.trim(),
    });
    track('generation.created', {
      jobId: job.id,
      productUid: product.uid,
      task,
    });
  }

  const result = generate.data;
  const sourceImage = product?.primaryImageUrl ?? null;

  return (
    <SidePanelModal
      open={open}
      onOpenChange={onOpenChange}
      ariaLabel="Studio"
      width="wide"
      header={
        <div className="flex flex-col gap-1">
          <h2 className="display text-display-md text-fg">Studio</h2>
          <p className="text-xs text-fg-3">
            {product ? `Editing · ${product.title}` : 'Select a product to generate from'}
          </p>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-6 px-5 py-5 text-sm">
        <section className="flex flex-col gap-3">
          <h3 className="text-md font-medium text-fg">Source</h3>
          <div className="rounded-lg border border-border bg-surface-2 p-3">
            {sourceImage ? (
              <img
                src={sourceImage}
                alt={product?.title ?? ''}
                className="aspect-square w-full rounded-md object-cover"
              />
            ) : (
              <div className="aspect-square w-full rounded-md bg-surface-3" aria-hidden />
            )}
          </div>
        </section>

        {productDrafts.length > 0 ? (
          <section className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
              <h3 className="text-md font-medium text-fg">Recent drafts</h3>
              <span className="text-[10px] text-fg-3">Click to reuse the prompt + mode</span>
            </div>
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {productDrafts.map((draft) => (
                <button
                  key={draft.id}
                  type="button"
                  onClick={() => loadDraft(draft)}
                  aria-label={`Load draft ${draft.id}`}
                  title={draft.originalPrompt}
                  className="group relative shrink-0 rounded-md border border-border bg-surface-2 p-1 transition-colors hover:border-border-strong focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <img
                    src={draft.outputImageUrl as string}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-16 w-16 rounded object-cover"
                  />
                  <span className="absolute inset-x-1 bottom-1 truncate rounded bg-bg/80 px-1 text-[9px] uppercase tracking-wide text-fg-2 backdrop-blur">
                    {draft.task}
                  </span>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        <section className="flex flex-col gap-3">
          <h3 className="text-md font-medium text-fg">Mode</h3>
          <div className="flex flex-wrap gap-2">
            {GENERATE_TASKS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTask(t)}
                aria-pressed={task === t}
                className="focus-visible:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <Chip
                  variant={task === t ? 'soft' : 'outline'}
                  tone={task === t ? 'accent' : 'neutral'}
                  size="md"
                >
                  {TASK_LABELS[t]}
                </Chip>
              </button>
            ))}
          </div>
          <p className="text-xs text-fg-3">{TASK_HINTS[task]}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-md font-medium text-fg">Prompt</h3>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.currentTarget.value)}
            placeholder="Studio sweep, soft daylight, slight grain, warm cast."
            maxLength={PROMPT_MAX_LENGTH}
            rows={4}
          />
          <p className="text-[10px] text-fg-3">
            {prompt.length} / {PROMPT_MAX_LENGTH}
          </p>
        </section>

        {result ? (
          <section className="flex flex-col gap-3">
            <h3 className="text-md font-medium text-fg">Result</h3>
            <div className="rounded-lg border border-border bg-surface-2 p-3">
              {result.outputImageUrl ? (
                <img
                  src={result.outputImageUrl}
                  alt="Generated"
                  className="aspect-square w-full rounded-md object-cover"
                />
              ) : null}
            </div>
            <div className="flex items-center gap-2 text-xs text-fg-3">
              <Chip size="xs" variant="soft" tone="success">
                {result.status.toLowerCase()}
              </Chip>
              <Chip size="xs" variant="solid" tone="neutral">
                {result.aiProvider}
              </Chip>
              <span>· job {result.id}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  navigate('/gallery');
                }}
              >
                Open in Gallery
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!result.outputImageUrl}
                onClick={async () => {
                  if (!result.outputImageUrl) return;
                  try {
                    await navigator.clipboard.writeText(result.outputImageUrl);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1500);
                  } catch {
                    // Clipboard API unavailable — silently no-op; the URL is still
                    // visible to the user via right-click → Copy on the result image.
                  }
                }}
              >
                {copied ? 'Copied!' : 'Copy image URL'}
              </Button>
            </div>
          </section>
        ) : null}

        {generate.error ? (
          <div
            role="alert"
            className="flex items-start justify-between gap-3 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger"
          >
            <div className="min-w-0">
              <p className="font-medium">Generation failed</p>
              <p className="mt-0.5 break-words text-xs text-danger/80">
                {humanizeError(generate.error)}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                if (generate.variables) generate.mutate(generate.variables);
              }}
              disabled={generate.isPending || !generate.variables}
            >
              Retry
            </Button>
          </div>
        ) : null}

        <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center justify-end gap-3 border-t border-border-subtle bg-bg/95 px-5 py-3 backdrop-blur">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!product || prompt.trim().length === 0 || generate.isPending}
          >
            {generate.isPending ? 'Generating…' : 'Generate'}
          </Button>
        </div>
      </form>
    </SidePanelModal>
  );
}
